import { useEffect, useRef } from "react";
import { useForm } from "react-hook-form";

import { DEFAULT_DOCUMENT_FILTERS, DocumentFilters } from "components/documents/utils";
import { camelToSentence } from "lib/utils";
import { useFormAutoSubmit } from "../../lib/hooks/useFormAutoSubmit";
import FilterCollapse from "../filter_form/filter_collapse/FilterCollapse";
import FilterCollapseHeader from "../filter_form/filter_collapse_header/FilterCollapseHeader";
import FilterFormTitle from "../filter_form/filter_form_title/FilterFormTitle";
import { countFilters } from "../filter_form/utils";
import { documentFilterContents } from "./filter_groups";
import { useDocumentsTracking } from "./tracking";
import { useSavedViews } from "./useDocumentsPageState";

type FilterGroupKeys = keyof typeof documentFilterContents;

const groupedKeys: Record<FilterGroupKeys, (keyof DocumentFilters)[]> = {
  keywords: ["keywords"],
  documentType: ["categories"],
  buyers: ["organisationIds", "buyerListIds", "buyerTypes", "buyerCountryRegions"],
  publishedDate: ["publishDate"],
};

// The URL drops empty filters, so a cleared filter arrives as an absent key. Resetting with these
// values fills those gaps; a missing key would otherwise leave its field showing its old value.
const EMPTY_FILTER_FIELDS = {
  keywords: [],
  categories: [],
  organisationIds: [],
  buyerListIds: [],
  buyerTypes: [],
  buyerCountryRegions: { selected: [], countries: [], regions: [] },
  publishDate: { filter: {} },
} satisfies Partial<DocumentFilters>;

export type FilterOptions = {
  disabledFields?: (keyof DocumentFilters)[];
  disabledSections?: FilterGroupKeys[];
  hiddenFilters?: (keyof DocumentFilters)[];
};

type FilterFormProps = {
  onChange: (filters: DocumentFilters) => void;
  filters: DocumentFilters;
  options?: FilterOptions;
  onClose: () => void;
  activeGroups: string[];
  onActiveGroupsChange: (group: string[]) => void;
  resetKey?: string;
  isEmptyState?: boolean;
  defaultFilters?: DocumentFilters;
  isSidebar?: boolean;
  onSideBarOpenChange?: (open: boolean) => void;
};

export function FilterForm({
  onChange,
  filters,
  options,
  onClose,
  activeGroups,
  onActiveGroupsChange,
  resetKey,
  isEmptyState,
  defaultFilters,
  isSidebar,
  onSideBarOpenChange,
}: FilterFormProps) {
  const previous = useRef(filters);
  const effectiveDefaultFilters = defaultFilters ?? DEFAULT_DOCUMENT_FILTERS;

  const tracking = useDocumentsTracking();

  const { control, watch, getValues, trigger, reset, setValue } = useForm<DocumentFilters>({
    mode: "onChange",
    defaultValues: filters,
  });

  useEffect(() => {
    // Because the filter form is no longer de-rendered when the view changes, we need to
    // reset the form when the view changes.
    if (resetKey) {
      reset({ ...EMPTY_FILTER_FIELDS, ...filters });
    }
  }, [resetKey, filters, reset]);

  const data = watch();

  const { setShowNoView, setFilters, setSelectedView, getAppliedFiltersCount } = useSavedViews(
    effectiveDefaultFilters,
    false,
  );

  function applyChange(data: DocumentFilters) {
    tracking.filterFormChanged(previous.current, data);
    previous.current = data;

    onChange(data);
  }

  useFormAutoSubmit({
    watch,
    getValues,
    trigger,
    onChange: applyChange,
  });

  const counts = countFilters(filters, groupedKeys);

  function clearFilter(key: FilterGroupKeys) {
    const emptyFields: Partial<DocumentFilters> = EMPTY_FILTER_FIELDS;
    groupedKeys[key].forEach((k) => {
      // The default `publishDate: {}` has no `filter`, which would leave the date range showing.
      const value =
        k === "publishDate"
          ? { filter: effectiveDefaultFilters.publishDate?.filter ?? {} }
          : effectiveDefaultFilters[k] ?? emptyFields[k];
      setValue(k, value);
    });

    // setValue doesn't emit a "change" event, so the auto-submit won't apply this on its own.
    void trigger().then((isValid) => {
      if (isValid) {
        applyChange(getValues());
      }
    });
  }

  const filterProps = {
    control,
    data,
    options,
  };

  function getFilterGroups() {
    const items = (Object.keys(groupedKeys) as FilterGroupKeys[]).map((key) => {
      const ContentComponent = documentFilterContents[key];

      return {
        key,
        header: (
          <FilterCollapseHeader
            title={camelToSentence(key)}
            count={counts[key]}
            clear={() => clearFilter(key)}
          />
        ),
        content: <ContentComponent {...filterProps} />,
      };
    });

    const disabledSections = options?.disabledSections || [];
    return items.filter((item) => !disabledSections.includes(item.key));
  }

  const items = getFilterGroups();

  function clearAll() {
    reset({ ...effectiveDefaultFilters });

    setFilters(effectiveDefaultFilters);
    setSelectedView(undefined);

    setTimeout(() => setShowNoView(true), 320); // ensuring happens after reset, otherwise looks odd as happens in two stages
  }

  return (
    <form aria-label="Document filters">
      <FilterFormTitle
        clearFilters={clearAll}
        showClear={Object.values(counts).some(Boolean)}
        onClose={onClose}
        isSidebar={isSidebar}
        isEmptyState={isEmptyState}
        filtersCount={getAppliedFiltersCount(filters)}
        onSideBarOpenChange={onSideBarOpenChange}
      />
      <FilterCollapse items={items} value={activeGroups} onChange={onActiveGroupsChange} />
    </form>
  );
}
